import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import "../style/header.css";
import logo from "../Assets/Bonzerlogo.png";

const NAV_LINKS = [
    { hash: "#home", label: "Home" },
    { hash: "#products", label: "Products" },
    { hash: "#Testimonials", label: "Testimonials" },
    { hash: "#aboutUs", label: "About us" },
    { hash: "#location", label: "Location" },
    { hash: "#contactUs", label: "Contact us" },
];

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    const [activeHash, setActiveHash] = useState(
        typeof window !== "undefined" && window.location.hash
            ? window.location.hash
            : "#home"
    );

    const toggleRef = useRef(null);
    const closeBtnRef = useRef(null);

    /* =========================================
       MOBILE MENU SCROLL LOCK

       IMPORTANT:
       Do NOT use position: fixed here.
       It causes mobile smooth-scroll problems.
    ========================================= */

    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = "hidden";
            document.documentElement.style.overflow = "hidden";

            closeBtnRef.current?.focus();
        } else {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
        };
    }, [isMenuOpen]);

    /* =========================================
       HEADER SCROLL
    ========================================= */

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 12);
        };

        handleScroll();

        window.addEventListener(
            "scroll",
            handleScroll,
            { passive: true }
        );

        return () => {
            window.removeEventListener(
                "scroll",
                handleScroll
            );
        };
    }, []);

    /* =========================================
       HASH CHANGE
    ========================================= */

    useEffect(() => {
        const handleHashChange = () => {
            setActiveHash(
                window.location.hash || "#home"
            );
        };

        window.addEventListener(
            "hashchange",
            handleHashChange
        );

        return () => {
            window.removeEventListener(
                "hashchange",
                handleHashChange
            );
        };
    }, []);

    /* =========================================
       ESCAPE KEY
    ========================================= */

    useEffect(() => {
        if (!isMenuOpen) return;

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                closeMenu();
            }
        };

        window.addEventListener(
            "keydown",
            handleKeyDown
        );

        return () => {
            window.removeEventListener(
                "keydown",
                handleKeyDown
            );
        };
    }, [isMenuOpen]);

    /* =========================================
       MENU TOGGLE
    ========================================= */

    const toggleMenu = () => {
        setIsMenuOpen((prev) => !prev);
    };

    /* =========================================
       CLOSE MENU
    ========================================= */

    const closeMenu = () => {
        setIsMenuOpen(false);

        requestAnimationFrame(() => {
            toggleRef.current?.focus();
        });
    };

    /* =========================================
       NAVIGATION

       This handles BOTH desktop and mobile.
    ========================================= */

    const handleNavClick = (e, hash) => {
        e.preventDefault();

        const targetId = hash.substring(1);

        setActiveHash(hash);

        /*
         * IMPORTANT:
         * Unlock scrolling BEFORE starting
         * the smooth scroll.
         */
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";

        /*
         * Close mobile menu.
         */
        setIsMenuOpen(false);

        /*
         * Wait for React/browser to remove the
         * menu overlay before scrolling.
         */
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {

                /*
                 * HOME
                 *
                 * Use window.scrollTo instead of
                 * scrollIntoView.
                 */
                if (targetId === "home") {

                    window.scrollTo({
                        top: 0,
                        left: 0,
                        behavior: "smooth",
                    });

                } else {

                    const target =
                        document.getElementById(
                            targetId
                        );

                    if (target) {

                        target.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                        });

                    }
                }

                /*
                 * Update URL without causing
                 * another browser scroll.
                 */
                window.history.replaceState(
                    null,
                    "",
                    hash
                );
            });
        });
    };

    /* =========================================
       LOGO → HOME
    ========================================= */

    const handleLogoClick = (e) => {
        e.preventDefault();

        setActiveHash("#home");

        /*
         * Unlock page scrolling.
         */
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";

        /*
         * Close mobile menu.
         */
        setIsMenuOpen(false);

        /*
         * Smoothly go to Home.
         */
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {

                window.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: "smooth",
                });

                window.history.replaceState(
                    null,
                    "",
                    "#home"
                );
            });
        });
    };

    return (
        <header
            className={`header${
                isScrolled
                    ? " isScrolled"
                    : ""
            }`}
        >

            {/* =====================================
                LOGO
            ====================================== */}

            <img
                className="logoImg"
                src={logo}
                alt="Bonzer logo"
                onClick={handleLogoClick}
            />

            {/* =====================================
                DESKTOP MENU
            ====================================== */}

            <nav
                className="li"
                aria-label="Primary"
            >
                {NAV_LINKS.map((link) => (
                    <a
                        key={link.hash}
                        href={link.hash}
                        className={
                            activeHash === link.hash
                                ? "isActive"
                                : ""
                        }
                        onClick={(e) =>
                            handleNavClick(
                                e,
                                link.hash
                            )
                        }
                    >
                        {link.label}
                    </a>
                ))}
            </nav>

            {/* =====================================
                MOBILE MENU
            ====================================== */}

            <div className="mobileMenu">

                <button
                    ref={toggleRef}
                    type="button"
                    className={`menuIcon${
                        isMenuOpen
                            ? " isOpen"
                            : ""
                    }`}
                    onClick={toggleMenu}
                    aria-label={
                        isMenuOpen
                            ? "Close menu"
                            : "Open menu"
                    }
                    aria-expanded={isMenuOpen}
                    aria-controls="mobileNav"
                >
                    <span />
                    <span />
                    <span />
                </button>

                <AnimatePresence>
                    {isMenuOpen && (
                        <>
                            {/* =================================
                                MOBILE OVERLAY
                            ================================== */}

                            <motion.div
                                className="mobileOverlay"
                                initial={{
                                    opacity: 0,
                                }}
                                animate={{
                                    opacity: 1,
                                }}
                                exit={{
                                    opacity: 0,
                                }}
                                transition={{
                                    duration: 0.25,
                                }}
                                onClick={closeMenu}
                            />

                            {/* =================================
                                MOBILE NAV
                            ================================== */}

                            <motion.nav
                                id="mobileNav"
                                className="mobileLi"
                                aria-label="Mobile"
                                initial={{
                                    x: "100%",
                                }}
                                animate={{
                                    x: 0,
                                }}
                                exit={{
                                    x: "100%",
                                }}
                                transition={{
                                    type: "spring",
                                    stiffness: 340,
                                    damping: 34,
                                }}
                            >

                                {/* CLOSE BUTTON */}

                                <button
                                    ref={closeBtnRef}
                                    type="button"
                                    className="mobileLiClose"
                                    onClick={closeMenu}
                                    aria-label="Close menu"
                                >
                                    <span />
                                    <span />
                                </button>

                                {/* NAV LINKS */}

                                {NAV_LINKS.map(
                                    (link, i) => (
                                        <motion.a
                                            key={
                                                link.hash
                                            }
                                            href={
                                                link.hash
                                            }
                                            className={
                                                activeHash ===
                                                link.hash
                                                    ? "isActive"
                                                    : ""
                                            }
                                            onClick={(e) =>
                                                handleNavClick(
                                                    e,
                                                    link.hash
                                                )
                                            }
                                            initial={{
                                                opacity: 0,
                                                y: 16,
                                            }}
                                            animate={{
                                                opacity: 1,
                                                y: 0,
                                            }}
                                            transition={{
                                                delay:
                                                    0.08 +
                                                    i * 0.05,
                                                duration: 0.35,
                                            }}
                                        >
                                            <span className="mobileLiIndex">
                                                {String(
                                                    i + 1
                                                ).padStart(
                                                    2,
                                                    "0"
                                                )}
                                            </span>

                                            {link.label}
                                        </motion.a>
                                    )
                                )}

                            </motion.nav>
                        </>
                    )}
                </AnimatePresence>
            </div>
        </header>
    );
};

export default Header;